import { AnyFeatureHook, useHeadlessQuery } from '@common/hooks';
import { UrlSchema, useUrlState } from '@common/hooks/useUrlState';
import {
    BaseFeatureArgs,
    FixedFilterGenerator,
    QueryManagerOptions,
} from '@common/types/queryState';
import { QuickFilterItem } from '@common/components/QuickFilterItem';
import { Clip } from '@features/Clip';
import {
    ArchiveIcon,
    CheckIcon,
    PlayIcon,
    TrashIcon,
} from '@radix-ui/react-icons';
import { Flex } from '@radix-ui/themes';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type ClipQuickFilters = 'all' | 'deleted' | 'available' | 'proxy';

interface ClipQuickFiltersUrlState {
    filter: ClipQuickFilters;
}

const filterSchema: UrlSchema<ClipQuickFiltersUrlState> = {
    filter: (val) => {
        if (val === 'all' || val === 'deleted' || val === 'proxy') return val;
        return 'available';
    },
};

const FILTER_DELETED = 'cf_s_value_MPMStatus:("Erased" "Deleting")';
const FILTER_AVAILABLE = '-cf_s_value_MPMStatus:("Erased" "Deleting")';
const FILTER_PROXY =
    '(materialInfo_flag_onFlow:PROXY* OR materialInfo_flag_online:PROXY*)';

const STATUS_FILTERS: Record<ClipQuickFilters, string[]> = {
    available: [FILTER_AVAILABLE],
    deleted: [FILTER_DELETED],
    proxy: [FILTER_AVAILABLE, FILTER_PROXY],
    all: [],
};

export const useClipQuickFilters = <TArgs extends BaseFeatureArgs>(
    queryHook: AnyFeatureHook<Clip, TArgs>,
    baseFeatureArgs?: Partial<TArgs>,
    options?: QueryManagerOptions,
) => {
    const { t } = useTranslation(['clipTable']);

    const { state, setState } = useUrlState<ClipQuickFiltersUrlState>(
        filterSchema,
        { filter: 'available' },
    );
    const filter = state.filter;

    const handleStatusChange = useCallback(
        (newStatus: ClipQuickFilters) => {
            setState({
                filter: newStatus === 'available' ? undefined : newStatus,
            });
        },
        [setState],
    );

    const filterGenerator: FixedFilterGenerator<TArgs> = useCallback(
        () => STATUS_FILTERS[filter] || [],
        [filter],
    );

    const headlessOptions = useMemo(
        () => ({
            ...options,
            paramsOverride: { facets: {}, ranges: {} },
        }),
        [options],
    );

    const createQueryArgs = useCallback(
        (generators: FixedFilterGenerator[]): TArgs =>
            ({
                ...baseFeatureArgs,
                fixedFilterGenerators: generators,
            }) as TArgs,
        [baseFeatureArgs],
    );

    const { itemCount: allCount, isFetching: isFetchingAll } = useHeadlessQuery(
        queryHook,
        { pageSize: 0 },
        createQueryArgs([]),
        headlessOptions,
    );

    const { itemCount: deletedCount, isFetching: isFetchingDeleted } =
        useHeadlessQuery(
            queryHook,
            { pageSize: 0 },
            createQueryArgs([() => [FILTER_DELETED]]),
            headlessOptions,
        );

    const { itemCount: proxyCount, isFetching: isFetchingProxy } =
        useHeadlessQuery(
            queryHook,
            { pageSize: 0 },
            createQueryArgs([() => [FILTER_AVAILABLE, FILTER_PROXY]]),
            headlessOptions,
        );

    const availableCount = useMemo(() => {
        if (allCount === undefined || deletedCount === undefined)
            return undefined;
        return Math.max(0, allCount - deletedCount);
    }, [allCount, deletedCount]);

    const filterConfigs = useMemo(
        () => [
            {
                value: 'all' as const,
                Icon: ArchiveIcon,
                label: t('quickFilters.all'),
                count: allCount,
                isLoading: isFetchingAll,
            },
            {
                value: 'deleted' as const,
                Icon: TrashIcon,
                label: t('quickFilters.deleted'),
                count: deletedCount,
                isLoading: isFetchingDeleted,
            },
            {
                value: 'available' as const,
                Icon: CheckIcon,
                label: t('quickFilters.available'),
                count: availableCount,
                isLoading: isFetchingAll || isFetchingDeleted,
            },
            {
                value: 'proxy' as const,
                Icon: PlayIcon,
                label: t('quickFilters.proxy'),
                count: proxyCount,
                isLoading: isFetchingProxy,
            },
        ],
        [
            t,
            allCount,
            isFetchingAll,
            deletedCount,
            isFetchingDeleted,
            availableCount,
            proxyCount,
            isFetchingProxy,
        ],
    );

    const quickFiltersController = (
        <Flex direction='column' gap='0' mb='3'>
            {filterConfigs.map((config) => (
                <QuickFilterItem
                    key={config.value}
                    {...config}
                    isActive={filter === config.value}
                    onClick={handleStatusChange}
                />
            ))}
        </Flex>
    );

    return {
        filterGenerator,
        quickFiltersController,
        status: filter,
    };
};
